// CASL footer (spec 08 §3): added to marketing and sequence kinds; cannot be removed.
import { consentReason } from "./consent.ts";
import { escapeHtml as e, isHttpsUrl } from "./render.ts";
import type { ConsentBasis, Lang, TemplateKind } from "./types.ts";

/** Send-path guard: marketing/sequence output must carry unsubscribe URL, postal address and sender name. */
export function assertCaslFooterPresent(
  html: string,
  text: string,
  kind: TemplateKind,
  expected: {
    unsubscribeUrl: string;
    postalAddress: string;
    legalName: string;
  },
): void {
  if (!needsCaslFooter(kind)) return;
  const needles = [
    expected.unsubscribeUrl,
    expected.postalAddress,
    expected.legalName,
  ];
  const ok =
    needles.every((n) => n.trim() !== "") &&
    needles.every((n) => html.includes(e(n))) &&
    needles.every((n) => text.includes(n));
  if (!ok) throw new Error("casl_footer_missing");
}

export const needsCaslFooter = (kind: TemplateKind): boolean =>
  kind === "marketing" || kind === "sequence";

export type FooterInput = {
  kind: TemplateKind;
  lang: Lang;
  legalName: string;
  postalAddress: string;
  contactEmail: string;
  consentBasis: ConsentBasis | null;
  unsubscribeUrl: string;
};

const COPY = {
  fr: {
    because: "Vous recevez ce courriel car",
    unsubscribe: "Se désabonner",
    sep: " : ",
  },
  en: {
    because: "You are receiving this email because",
    unsubscribe: "Unsubscribe",
    sep: ": ",
  },
} as const;

export function buildCaslFooter(
  i: FooterInput,
): { html: string; text: string } | null {
  if (!needsCaslFooter(i.kind)) return null;
  if (!i.postalAddress.trim()) throw new Error("postal_address_missing");
  if (!i.unsubscribeUrl) throw new Error("unsubscribe_url_missing");
  if (!isHttpsUrl(i.unsubscribeUrl)) throw new Error("unsubscribe_url_unsafe");
  const c = COPY[i.lang];
  const reason = consentReason(i.consentBasis, i.lang);
  const html =
    `<p style="margin:0 0 8px 0;">${e(i.legalName)} · ${e(i.postalAddress)} · ` +
    `<a href="mailto:${e(i.contactEmail)}" style="color:#6B6B6B;">${e(i.contactEmail)}</a></p>` +
    `<p style="margin:0;">${e(c.because)} ${e(reason)}. ` +
    `<a href="${e(i.unsubscribeUrl)}" style="color:#F77119;">${e(c.unsubscribe)}</a></p>`;
  const text =
    `${i.legalName} · ${i.postalAddress} · ${i.contactEmail}\n` +
    `${c.because} ${reason}. ${c.unsubscribe}${c.sep}${i.unsubscribeUrl}`;
  return { html, text };
}
